import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { OutgoingWebhook, IntegrationLog } from '../types';
import { WEBHOOK_EVENTS } from '../types';

const STATUS_META = {
  success:  { label: 'Sucesso',      color: 'bg-emerald-500/20 text-emerald-400', icon: 'ri-checkbox-circle-line' },
  error:    { label: 'Erro',         color: 'bg-red-500/20 text-red-400',         icon: 'ri-close-circle-line' },
  untested: { label: 'Não testado',  color: 'bg-white/10 text-white/40',          icon: 'ri-question-line' },
};

const emptyWebhook = (): Omit<OutgoingWebhook, 'id' | 'created_at' | 'updated_at'> => ({
  name: '',
  endpoint_url: '',
  secret: '',
  events: [],
  is_active: true,
  description: '',
  last_triggered_at: null,
  last_status: 'untested',
});

interface Props {
  logs: IntegrationLog[];
  onRefreshLogs: () => void;
}

export const WebhooksIntegration = ({ logs, onRefreshLogs }: Props) => {
  const [webhooks, setWebhooks] = useState<OutgoingWebhook[]>([]);
  const [editing, setEditing] = useState<Partial<OutgoingWebhook> | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { ok: boolean; message: string }>>({});
  const [showSecret, setShowSecret] = useState(false);
  const [selectedLog, setSelectedLog] = useState<IntegrationLog | null>(null);
  const [expandedLogs, setExpandedLogs] = useState<string | null>(null);

  const fetchWebhooks = useCallback(async () => {
    const { data } = await supabase.from('outgoing_webhooks').select('*').order('created_at');
    setWebhooks((data as OutgoingWebhook[]) || []);
  }, []);

  useEffect(() => { fetchWebhooks(); }, [fetchWebhooks]);

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);

    if (isNew) {
      const { error } = await supabase.from('outgoing_webhooks').insert({
        name: editing.name,
        endpoint_url: editing.endpoint_url,
        secret: editing.secret || null,
        events: editing.events || [],
        is_active: editing.is_active ?? true,
        description: editing.description || null,
      });
      if (!error) { await fetchWebhooks(); setEditing(null); setIsNew(false); }
    } else {
      const { error } = await supabase.from('outgoing_webhooks').update({
        name: editing.name,
        endpoint_url: editing.endpoint_url,
        secret: editing.secret || null,
        events: editing.events || [],
        is_active: editing.is_active,
        description: editing.description || null,
        updated_at: new Date().toISOString(),
      }).eq('id', editing.id);
      if (!error) { setWebhooks((prev) => prev.map((w) => (w.id === editing.id ? { ...w, ...editing } as OutgoingWebhook : w))); setEditing(null); }
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    await supabase.from('outgoing_webhooks').delete().eq('id', id);
    setWebhooks((prev) => prev.filter((w) => w.id !== id));
    if (editing?.id === id) setEditing(null);
  };

  const handleTest = async (webhook: OutgoingWebhook) => {
    setTesting(webhook.id);
    try {
      const payload = {
        event: 'webhook.test',
        timestamp: new Date().toISOString(),
        data: { message: 'Teste de conectividade do webhook' },
      };

      const response = await fetch(webhook.endpoint_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(webhook.secret ? { 'X-Webhook-Secret': webhook.secret } : {}),
        },
        body: JSON.stringify(payload),
      });

      const ok = response.ok;
      const message = ok
        ? `Webhook testado com sucesso (HTTP ${response.status})`
        : `Falha no teste (HTTP ${response.status})`;

      setTestResults((prev) => ({ ...prev, [webhook.id]: { ok, message } }));

      await supabase.from('outgoing_webhooks').update({
        last_triggered_at: new Date().toISOString(),
        last_status: ok ? 'success' : 'error',
      }).eq('id', webhook.id);

      await supabase.from('integration_logs').insert({
        webhook_id: webhook.id,
        event_type: 'webhook.test',
        status: ok ? 'success' : 'error',
        message,
        metadata: { status_code: response.status },
      });

      setWebhooks((prev) => prev.map((w) => w.id === webhook.id ? { ...w, last_status: ok ? 'success' : 'error', last_triggered_at: new Date().toISOString() } : w));
      onRefreshLogs();
    } catch (err) {
      const message = `Erro de conexão: ${err instanceof Error ? err.message : 'Falha desconhecida'}`;
      setTestResults((prev) => ({ ...prev, [webhook.id]: { ok: false, message } }));
      await supabase.from('integration_logs').insert({
        webhook_id: webhook.id,
        event_type: 'webhook.test',
        status: 'error',
        message,
        metadata: {},
      });
      onRefreshLogs();
    }
    setTesting(null);
  };

  const toggleEvent = (event: string) => {
    if (!editing) return;
    const events = editing.events || [];
    const updated = events.includes(event) ? events.filter((e) => e !== event) : [...events, event];
    setEditing({ ...editing, events: updated });
  };

  const statusMeta = (status: string) => STATUS_META[status as keyof typeof STATUS_META] || STATUS_META.untested;
  const webhookLogs = (webhookId: string) => logs.filter((l) => l.webhook_id === webhookId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[15px] font-700 text-white">Webhooks de Saída</h3>
          <p className="text-[12px] text-white/40 mt-0.5">Configure endpoints que receberão eventos do sistema</p>
        </div>
        <button
          onClick={() => { setEditing(emptyWebhook()); setIsNew(true); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line text-[14px]" />
          Novo Webhook
        </button>
      </div>

      {/* Webhooks list */}
      {webhooks.length === 0 && !editing && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center">
          <div className="w-16 h-16 mx-auto bg-white/5 rounded-2xl flex items-center justify-center mb-4">
            <i className="ri-webhook-line text-white/20 text-[28px]" />
          </div>
          <p className="text-[14px] font-600 text-white/40 mb-1">Nenhum webhook configurado</p>
          <p className="text-[12px] text-white/25">Crie webhooks para notificar sistemas externos sobre eventos do SaaS</p>
        </div>
      )}

      {webhooks.map((webhook) => {
        const sm = statusMeta(webhook.last_status || 'untested');
        const wLogs = webhookLogs(webhook.id);
        const testRes = testResults[webhook.id];
        const isExpanded = expandedLogs === webhook.id;
        return (
          <div key={webhook.id} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${webhook.is_active ? 'bg-violet-600/20' : 'bg-white/5'}`}>
                    <i className={`ri-webhook-line text-[18px] ${webhook.is_active ? 'text-violet-400' : 'text-white/30'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h4 className="text-[14px] font-700 text-white">{webhook.name}</h4>
                      <span className={`text-[10px] font-700 px-2 py-0.5 rounded-full flex items-center gap-1 ${sm.color}`}>
                        <i className={`${sm.icon} text-[11px]`} />
                        {sm.label}
                      </span>
                      {!webhook.is_active && (
                        <span className="text-[10px] font-700 px-2 py-0.5 rounded-full bg-white/10 text-white/30">Inativo</span>
                      )}
                    </div>
                    <p className="text-[12px] text-white/40 font-mono truncate">{webhook.endpoint_url}</p>
                    {webhook.description && (
                      <p className="text-[11px] text-white/30 mt-1">{webhook.description}</p>
                    )}
                    {webhook.events.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {webhook.events.map((ev) => (
                          <span key={ev} className="text-[10px] font-600 px-2 py-0.5 bg-violet-500/10 text-violet-400 rounded-full">
                            {WEBHOOK_EVENTS.find((e) => e.value === ev)?.label || ev}
                          </span>
                        ))}
                      </div>
                    )}
                    {webhook.last_triggered_at && (
                      <p className="text-[11px] text-white/20 mt-2">
                        Último disparo: {new Date(webhook.last_triggered_at).toLocaleString('pt-BR')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                  <button
                    onClick={() => handleTest(webhook)}
                    disabled={testing === webhook.id}
                    className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/50 text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${testing === webhook.id ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} text-[13px]`} />
                    {testing === webhook.id ? 'Testando...' : 'Testar'}
                  </button>
                  <button
                    onClick={() => { setEditing({ ...webhook }); setIsNew(false); }}
                    className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/50 transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-line text-[14px]" />
                  </button>
                  <button
                    onClick={() => setExpandedLogs(isExpanded ? null : webhook.id)}
                    className={`w-9 h-9 flex items-center justify-center border rounded-xl transition-colors cursor-pointer ${isExpanded ? 'bg-violet-600/20 border-violet-500/30 text-violet-400' : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'}`}
                  >
                    <i className={`ri-history-line text-[14px]`} />
                  </button>
                  <button
                    onClick={() => handleDelete(webhook.id)}
                    className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/30 rounded-xl text-white/30 hover:text-red-400 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-[14px]" />
                  </button>
                </div>
              </div>

              {testRes && (
                <div className={`mt-3 rounded-xl p-3 flex items-start gap-2 ${testRes.ok ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                  <i className={`text-[13px] mt-0.5 ${testRes.ok ? 'ri-checkbox-circle-line text-emerald-400' : 'ri-error-warning-line text-red-400'}`} />
                  <p className="text-[12px] text-white/70">{testRes.message}</p>
                </div>
              )}
            </div>

            {/* Logs expansion */}
            {isExpanded && (
              <div className="border-t border-white/10 p-5">
                <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider mb-3">
                  Histórico de Tentativas ({wLogs.length})
                </p>
                {wLogs.length === 0 ? (
                  <p className="text-[12px] text-white/20 italic">Nenhuma tentativa registrada ainda</p>
                ) : (
                  <div className="space-y-2">
                    {wLogs.slice(0, 10).map((log) => (
                      <button
                        key={log.id}
                        onClick={() => setSelectedLog(log)}
                        className="w-full flex items-center justify-between bg-white/5 hover:bg-white/10 rounded-xl px-4 py-2.5 transition-colors cursor-pointer text-left"
                      >
                        <div className="flex items-center gap-3">
                          <i className={`text-[13px] ${log.status === 'success' ? 'ri-checkbox-circle-line text-emerald-400' : 'ri-close-circle-line text-red-400'}`} />
                          <span className="text-[12px] text-white/70 font-mono">{log.event_type}</span>
                          {log.message && <span className="text-[11px] text-white/30 truncate max-w-[200px]">{log.message}</span>}
                        </div>
                        <span className="text-[11px] text-white/25 flex-shrink-0">{new Date(log.created_at).toLocaleString('pt-BR')}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Edit/Create form */}
      {editing && (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <h2 className="text-[15px] font-700 text-white">
              {isNew ? 'Novo Webhook' : `Editar: ${editing.name}`}
            </h2>
            <button onClick={() => { setEditing(null); setIsNew(false); }} className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer">
              <i className="ri-close-line text-white/60" />
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-4">
                <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider">Configuração</p>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Nome</label>
                  <input
                    type="text"
                    value={editing.name || ''}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="Ex: Notificações Slack"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20"
                  />
                </div>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">URL do Endpoint</label>
                  <input
                    type="url"
                    value={editing.endpoint_url || ''}
                    onChange={(e) => setEditing({ ...editing, endpoint_url: e.target.value })}
                    placeholder="https://hooks.slack.com/services/..."
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20"
                  />
                </div>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Segredo (Secret)</label>
                  <div className="relative">
                    <input
                      type={showSecret ? 'text' : 'password'}
                      value={editing.secret || ''}
                      onChange={(e) => setEditing({ ...editing, secret: e.target.value })}
                      placeholder="Opcional — enviado no header X-Webhook-Secret"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 pr-10"
                    />
                    <button onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer" type="button">
                      <i className={`${showSecret ? 'ri-eye-off-line' : 'ri-eye-line'} text-white/30 text-[14px]`} />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Descrição</label>
                  <textarea
                    value={editing.description || ''}
                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                    rows={2}
                    maxLength={200}
                    placeholder="Para que serve este webhook?"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 resize-none"
                  />
                </div>

                <div className="flex items-center justify-between py-3">
                  <p className="text-[13px] text-white font-600">Webhook ativo</p>
                  <button
                    onClick={() => setEditing({ ...editing, is_active: !editing.is_active })}
                    className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative flex-shrink-0 overflow-hidden ${editing.is_active ? 'bg-violet-600' : 'bg-white/10'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${editing.is_active ? 'left-6' : 'left-1'}`} />
                  </button>
                </div>
              </div>

              <div>
                <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider mb-3">Eventos a Disparar</p>
                <div className="space-y-2">
                  {WEBHOOK_EVENTS.map((ev) => {
                    const active = (editing.events || []).includes(ev.value);
                    return (
                      <button
                        key={ev.value}
                        onClick={() => toggleEvent(ev.value)}
                        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border transition-all cursor-pointer ${active ? 'bg-violet-600/10 border-violet-500/30' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                      >
                        <span className={`text-[13px] font-600 ${active ? 'text-white' : 'text-white/40'}`}>{ev.label}</span>
                        <span className="text-[10px] font-600 text-white/25 font-mono">{ev.value}</span>
                        <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ml-2 ${active ? 'bg-violet-600' : 'bg-white/10'}`}>
                          {active && <i className="ri-check-line text-white text-[12px]" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-white/10">
              <button onClick={() => { setEditing(null); setIsNew(false); }} className="px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/60 text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !editing.name?.trim() || !editing.endpoint_url?.trim()}
                className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                {saving ? 'Salvando...' : isNew ? 'Criar Webhook' : 'Salvar Alterações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log detail modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="text-[15px] font-700 text-white">Detalhe do Evento</h3>
              <button onClick={() => setSelectedLog(null)} className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer">
                <i className="ri-close-line text-white/60" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {[
                { label: 'Evento', value: selectedLog.event_type },
                { label: 'Status', value: selectedLog.status },
                { label: 'Mensagem', value: selectedLog.message || '—' },
                { label: 'Data/Hora', value: new Date(selectedLog.created_at).toLocaleString('pt-BR') },
              ].map((row) => (
                <div key={row.label} className="bg-white/5 rounded-xl p-3">
                  <p className="text-[11px] text-white/30 mb-1">{row.label}</p>
                  <p className="text-[12px] text-white font-500 break-all">{row.value}</p>
                </div>
              ))}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-[11px] text-white/30 mb-1">Metadata</p>
                  <pre className="text-[11px] text-white/60 font-mono whitespace-pre-wrap">{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
